<script setup>
import { nextTick, ref, watch } from "vue";
import { debounce } from "lodash";
import axios from "axios";
import { createPopper } from "@popperjs/core";
import SimpleLoadingCircle from "./SimpleLoadingCircle.vue";

const props = defineProps({
    apiUrl: {
        type: String,
        required: true,
    },
});

const query = ref("");
const showResults = ref(false);
const results = ref(null);
const isFetching = ref(false);

function trySearch(e) {
    if (query.value.trim().length <= 0) {
        tryClose();
        return;
    }
    isFetching.value = true;
    axios
        .get(props.apiUrl, {
            params: {
                query: query.value,
            },
        })
        .then((response) => {
            console.log(response.data);
            showResults.value = true;
            results.value = response.data;
        })
        .catch((error) => {
            console.log(error);
            showResults.value = false;
        })
        .finally(() => {
            isFetching.value = false;
        });
}

const debouncedSearch = debounce(trySearch, 1000);

const rootRef = ref(null);
const resultRef = ref(null);
const popperInstance = ref(null);
watch(showResults, (newValue) => {
    if (newValue) {
        nextTick(() => {
            popperInstance.value = createPopper(
                rootRef.value,
                resultRef.value,
                {
                    placement: "bottom",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 8],
                            },
                        },
                        {
                            name: "sameWidth",
                            enabled: true,
                            phase: "beforeWrite",
                            requires: ["computeStyles"],
                            fn({ state }) {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                            effect({ state }) {
                                state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                            },
                        },
                    ],
                }
            );
            popperInstance.value.forceUpdate();
        });
    } else {
        popperInstance.value.destroy();
    }
});

function tryClose() {
    showResults.value = false;
}

function tryReshow() {
    if (results.value && query.value.length > 0) {
        showResults.value = true;
    }
}

function isAllEmpty() {
    return (
        results.value?.teams?.length <= 0 &&
        results.value?.users?.length <= 0 &&
        results.value?.threads?.length <= 0 &&
        results.value?.plays?.length <= 0
    );
}
</script>

<template>
    <div
        class="bg-gray-100 rounded-full py-2 flex items-center gap-2 px-4"
        ref="rootRef"
    >
        <ion-icon name="search-outline" class="text-gray-500"></ion-icon>
        <input
            type="text"
            placeholder="Search..."
            class="
                bg-transparent
                outline-none
                w-full
                block
                flex-grow flex-shrink
            "
            v-model="query"
            @focus="tryReshow"
            @input="debouncedSearch"
        />
        <simple-loading-circle
            v-if="isFetching"
            class="text-primary"
            size="16"
        ></simple-loading-circle>
    </div>
    <teleport to="body">
        <div
            class="fixed inset-0 bg-black opacity-10"
            v-if="showResults"
            @click="tryClose"
        ></div>
        <div
            ref="resultRef"
            class="p-4 bg-white rounded-lg shadow max-h-[50vh] overflow-y-auto"
            v-show="showResults && results"
        >
            <div v-if="results?.teams?.length > 0" class="my-4">
                <h3 class="text-sm font-semibold ml-4 uppercase">Teams</h3>
                <ul class="my-2">
                    <li
                        v-for="team in results.teams"
                        class="
                            py-2
                            px-4
                            hover:bg-gray-100
                            rounded-lg
                            flex
                            items-center
                            gap-2
                        "
                    >
                        <div class="flex-grow flex-shrink">
                            <h4 class="text-sm">
                                {{ team.name }}
                            </h4>
                            <p class="text-sm text-gray-500">
                                {{ team.city?.name }}
                            </p>
                        </div>
                        <a
                            :href="`/messages?to=${team.owner.id}`"
                            class="
                                ml-auto
                                bg-transparent
                                p-2
                                rounded-full
                                border border-primary
                                text-primary text-xs
                            "
                        >
                            Message Owner
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="results?.users?.length > 0" class="my-4">
                <h3 class="text-sm font-semibold ml-4 uppercase">Users</h3>
                <ul class="my-2">
                    <li
                        v-for="user in results.users"
                        class="
                            py-2
                            px-4
                            hover:bg-gray-100
                            rounded-lg
                            flex
                            items-center
                            gap-2
                        "
                    >
                        <div class="flex-grow flex-shrink">
                            <h4 class="text-sm">
                                {{ user.name }}
                            </h4>
                        </div>
                        <a
                            :href="`/messages?to=${user.id}`"
                            class="
                                ml-auto
                                bg-transparent
                                p-2
                                rounded-full
                                border border-primary
                                text-primary text-xs
                            "
                        >
                            Message
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="results?.threads?.length > 0" class="my-4">
                <h3 class="text-sm font-semibold ml-4 uppercase">Forums</h3>
                <ul class="my-2">
                    <li
                        v-for="thread in results.threads"
                        class="
                            py-2
                            px-4
                            hover:bg-gray-100
                            rounded-lg
                            flex
                            items-center
                            gap-2
                        "
                    >
                        <div class="flex-grow flex-shrink">
                            <h4 class="text-sm">
                                {{ thread.title }}
                            </h4>
                        </div>
                        <a
                            :href="thread.url"
                            class="
                                ml-auto
                                bg-transparent
                                p-2
                                rounded-full
                                border border-primary
                                text-primary text-xs
                            "
                        >
                            Open
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="results?.plays?.length > 0" class="my-4">
                <h3 class="text-sm font-semibold ml-4 uppercase">Plays</h3>
                <ul class="my-2">
                    <li
                        v-for="play in results.plays"
                        class="
                            py-2
                            px-4
                            hover:bg-gray-100
                            rounded-lg
                            flex
                            items-center
                            gap-2
                        "
                    >
                        <div class="flex-grow flex-shrink">
                            <h4 class="text-sm">
                                {{ play.play_name }}
                            </h4>
                        </div>
                        <a
                            :href="play.url"
                            class="
                                ml-auto
                                bg-transparent
                                p-2
                                rounded-full
                                border border-primary
                                text-primary text-xs
                            "
                        >
                            Open
                        </a>
                    </li>
                </ul>
            </div>
            <div v-if="isAllEmpty()">
                <h3 class="text-sm text-center py-4 text-gray-500 uppercase">
                    No Results
                </h3>
            </div>
        </div>
    </teleport>
</template>

<style scoped></style>
